
import { useState } from 'react';

export default function ChecklistDownloadSection() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    numberOfEmployees: '',
    captcha: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [captchaQuestion, setCaptchaQuestion] = useState(() => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    return { num1, num2, answer: num1 + num2 };
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate captcha
    if (parseInt(formData.captcha) !== captchaQuestion.answer) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Submit form data
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('firstName', formData.firstName);
      formDataToSubmit.append('lastName', formData.lastName);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('companyName', formData.companyName);
      formDataToSubmit.append('numberOfEmployees', formData.numberOfEmployees);

      const response = await fetch('https://readdy.ai/api/form/d5rtubk98t1oshnrecvg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        
        // Trigger PDF download
        const link = document.createElement('a');
        link.href = 'https://vaoppjqfltabkawlwllk.supabase.co/storage/v1/object/public/papers/papers/1769466608968-Avert_Guide_Cyber_Insurance_Readiness.pdf';
        link.download = 'Healthcare-IT-Cyber-Insurance-Checklist.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Navigate to Cyber Insurance page after a brief delay
        setTimeout(() => {
          if (window.REACT_APP_NAVIGATE) {
            window.REACT_APP_NAVIGATE('/cyber-insurance-checklist');
          }
        }, 1000);

        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          companyName: '',
          numberOfEmployees: '',
          captcha: '',
        });
        
        // Generate new captcha
        const num1 = Math.floor(Math.random() * 10) + 1;
        const num2 = Math.floor(Math.random() * 10) + 1;
        setCaptchaQuestion({ num1, num2, answer: num1 + num2 });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 px-6 bg-gradient-to-br from-red-50 to-white">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#e10708] rounded-full mb-6">
            <i className="ri-shield-check-line text-3xl text-white"></i>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Download Your Free Cyber Insurance Readiness Checklist
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get our comprehensive checklist to ensure your healthcare practice meets all cyber insurance requirements and protects patient data effectively.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <form id="checklist-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm"
                  placeholder="John"
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm"
                placeholder="john.doe@example.com"
              />
            </div>

            <div>
              <label htmlFor="companyName" className="block text-sm font-semibold text-gray-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm"
                placeholder="Your Healthcare Practice"
              />
            </div>

            <div>
              <label htmlFor="numberOfEmployees" className="block text-sm font-semibold text-gray-700 mb-2">
                Number of Employees *
              </label>
              <select
                id="numberOfEmployees"
                name="numberOfEmployees"
                value={formData.numberOfEmployees}
                onChange={(e) => setFormData({ ...formData, numberOfEmployees: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm cursor-pointer"
              >
                <option value="">Select range</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-100">51-100 employees</option>
                <option value="101-250">101-250 employees</option>
                <option value="251-500">251-500 employees</option>
                <option value="500+">500+ employees</option>
              </select>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
              <label htmlFor="captcha" className="block text-sm font-semibold text-gray-700 mb-3">
                Security Verification *
              </label>
              <p className="text-gray-600 mb-3">
                Please solve this simple math problem to verify you're not a robot:
              </p>
              <div className="flex items-center gap-4">
                <span className="text-2xl font-bold text-[#e10708]">
                  {captchaQuestion.num1} + {captchaQuestion.num2} =
                </span>
                <input
                  type="number"
                  id="captcha"
                  name="captcha"
                  value={formData.captcha}
                  onChange={handleChange}
                  required
                  className="w-24 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm"
                  placeholder="?"
                />
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
                <div>
                  <p className="text-green-800 font-semibold">Success!</p>
                  <p className="text-green-700 text-sm">Your checklist is downloading.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-error-warning-fill text-red-600 text-xl"></i>
                <div>
                  <p className="text-red-800 font-semibold">Submission Failed</p>
                  <p className="text-red-700 text-sm">Please check your information and try again.</p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#e10708] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#c00607] transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-download-line"></i>
                  Download Free Checklist
                </span>
              )}
            </button>

            <p className="text-xs text-gray-500 text-center">
              By downloading, you agree to receive occasional emails from Healthcare IT Partners. 
              Your information will be sent to protectus@healthcareITPartners.com and kept confidential.
            </p>
          </form>
        </div>

        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-[#e10708]"></i>
              <span>HIPAA Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-lock-line text-[#e10708]"></i>
              <span>Secure & Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-mail-line text-[#e10708]"></i>
              <span>No Spam Guarantee</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
